import type { CycleCountConfig } from '../models/cycle-count-config.model';

/**
 * Shared cycle-count admin dashboard logic (#31395). The web admin page (web-warehouse) built
 * these metric cards inline; the mobile admin page (nativescript-tool-tracker) needs the same
 * numbers, and a lib cannot import from an app, so the logic lives here and both platforms
 * consume {@link buildCycleCountAdminMetrics}. Every date window takes an injectable `now` so
 * the behaviour is deterministic under test regardless of the machine timezone.
 */

/** The "Avg accuracy (30D)" card display. */
export interface AccuracyCardDisplay {
  value: string;
  subtitle: string;
  trendClass: string;
}

/** The four cycle-count admin dashboard cards, computed from the loaded warehouse configs. */
export interface CycleCountAdminMetrics {
  totalWarehouses: number;
  dueThisWeekCount: number;
  dueThisWeekLabel: string;
  recentCounts: number;
  accuracy: AccuracyCardDisplay;
}

/** Midnight (00:00:00.000) of the given date's day, in local time. */
export function startOfDay(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  );
}

/** Last millisecond (23:59:59.999) of the given date's day, in local time. */
export function endOfDay(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  );
}

/**
 * True when the config's next-scheduled date falls within [start of today, end of today+7].
 * A missing or unparseable date is never due.
 */
export function isCycleCountDueThisWeek(
  config: Pick<CycleCountConfig, 'nextScheduledDate'>,
  now: Date = new Date()
): boolean {
  if (!config.nextScheduledDate) return false;

  const next = new Date(config.nextScheduledDate);
  if (Number.isNaN(next.getTime())) return false;

  const weekFromNow = new Date(now);
  weekFromNow.setDate(now.getDate() + 7);

  return next >= startOfDay(now) && next <= endOfDay(weekFromNow);
}

/**
 * True when `dateStr` falls within [start of (now - days), end of now]. A missing or
 * unparseable date is never within the window.
 */
export function isLastRunWithinPastDays(
  dateStr: string | null,
  days: number,
  now: Date = new Date()
): boolean {
  if (!dateStr) return false;

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return false;

  const threshold = new Date(now);
  threshold.setDate(now.getDate() - days);

  return date >= startOfDay(threshold) && date <= endOfDay(now);
}

/**
 * The "due this week" summary label: the names of up to the first two due warehouses joined
 * by "·", or a placeholder when nothing is due.
 */
export function buildDueThisWeekLabel(
  dueConfigs: Pick<CycleCountConfig, 'warehouseName'>[]
): string {
  if (!dueConfigs.length) return 'Nothing due this week';
  return dueConfigs
    .slice(0, 2)
    .map((config) => config.warehouseName)
    .join(' · ');
}

/**
 * The "Avg accuracy (30D)" card. The real accuracy figure lives in the Nifty PowerBI Accuracy
 * Report, not in the loaded configs, so the card is a call-to-action that opens that report
 * (BUG-30362 — it previously rendered a hard-coded em-dash that read as broken). The subtitle
 * counts Complete sessions whose last run falls within the past 30 days.
 */
export function buildAccuracyCardDisplay(
  configs: Pick<CycleCountConfig, 'cycleCountStatus' | 'lastRunDate'>[],
  now: Date = new Date()
): AccuracyCardDisplay {
  const completedRecentCount = configs.filter(
    (config) =>
      config.cycleCountStatus === 'Complete' &&
      isLastRunWithinPastDays(config.lastRunDate, 30, now)
  ).length;

  return {
    value: 'View report',
    subtitle: completedRecentCount
      ? `${completedRecentCount} completed counts in 30 days`
      : 'No completed counts in the last 30 days',
    trendClass: 'metric-card__trend--muted',
  };
}

/**
 * Builds the four cycle-count admin dashboard cards from the loaded warehouse configs.
 * Consumed identically by the web (web-warehouse) and mobile (nativescript-tool-tracker)
 * admin screens so the dashboard cannot drift between platforms.
 */
export function buildCycleCountAdminMetrics(
  configs: CycleCountConfig[],
  now: Date = new Date()
): CycleCountAdminMetrics {
  const dueConfigs = configs.filter((config) =>
    isCycleCountDueThisWeek(config, now)
  );

  return {
    totalWarehouses: configs.length,
    dueThisWeekCount: dueConfigs.length,
    dueThisWeekLabel: buildDueThisWeekLabel(dueConfigs),
    recentCounts: configs.filter((config) =>
      isLastRunWithinPastDays(config.lastRunDate, 7, now)
    ).length,
    accuracy: buildAccuracyCardDisplay(configs, now),
  };
}
